import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { LangSwitch } from "@/components/Layout";
import { api, errMsg } from "@/lib/api";
import { getDeviceId, useSeo } from "@/lib/utils";
import { useAuth } from "@/context/AppContext";

const Shell = ({ title, sub, children }) => (
  <div className="grid min-h-screen md:grid-cols-2">
    <div className="relative hidden flex-col justify-between bg-[#0B192C] p-12 text-white md:flex z-noise">
      <Logo light size={52} />
      <div><h2 className="text-4xl font-black leading-tight">كل خدمة ... أقرب</h2><p className="mt-3 max-w-md text-white/70">ZEEVA PRO — منصة خدمات عند الطلب في الأردن. الطلب مجاني، والشريك المناسب أقرب مما تتخيل.</p></div>
      <p className="text-xs text-white/40">zeeva-pro.com</p>
    </div>
    <div className="flex flex-col p-6 md:p-12">
      <div className="flex items-center justify-between md:justify-end"><div className="md:hidden"><Logo /></div><LangSwitch /></div>
      <div className="mx-auto w-full max-w-md flex-1 py-10">
        <h1 className="text-3xl font-black text-[#0B192C]">{title}</h1><p className="mt-1 text-muted-foreground">{sub}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

export function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  useSeo({ title: t("auth.login") });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/login", (f.phone || "").includes("@") ? { email: f.phone.trim(), password: f.password } : { phone: f.phone, password: f.password }); login(data); const staff = ["super_admin", "admin", "operations", "finance", "support"].includes(data.user.role); const dest = loc.state?.from || (staff ? "/admin" : data.user.role === "partner" ? "/partner" : "/dashboard"); nav(dest, { state: loc.state }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("auth.welcome_back")} sub={t("auth.login")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><label className="z-label">{t("auth.phone")}</label><input data-testid="login-phone" className="z-input" type="text" inputMode="tel" autoComplete="username" dir="ltr" placeholder="07XXXXXXXX" required value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><label className="z-label">{t("auth.password")}</label><PasswordInput testId="login-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        <button type="submit" data-testid="login-submit" disabled={busy} className="z-btn-primary w-full">{t("auth.login")}</button>
      </form>
      <p className="mt-6 text-center text-sm">{t("auth.no_account")} <Link to="/register" state={loc.state} data-testid="go-register" className="font-bold text-[#06B6D4]">{t("auth.register")}</Link></p>
    </Shell>
  );
}

export function Register() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", role: loc.state?.role || "customer", accepted_terms: false });
  const [busy, setBusy] = useState(false);
  useSeo({ title: t("auth.register") });
  const submit = async (e) => {
    e.preventDefault(); if (!f.accepted_terms) return toast.error(t("auth.accept") + " " + t("auth.terms"));
    setBusy(true);
    try { const { data } = await api.post("/auth/register", { ...f, locale: i18n.language, device_id: getDeviceId() }); login(data); nav(loc.state?.from || (data.user.role === "partner" ? "/partner/profile" : "/dashboard"), { state: loc.state }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("auth.join")} sub={t("auth.register")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-2">{["customer", "partner"].map((r) => <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setF({ ...f, role: r })} className={`rounded-xl border-2 px-3 py-3 text-sm font-bold transition-colors ${f.role === r ? "border-[#06B6D4] bg-[#06B6D4]/10" : "hover:bg-muted"}`}>{t(`auth.${r}`)}</button>)}</div>
        <div><label className="z-label">{t("auth.name")}</label><input data-testid="register-name" className="z-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="z-label">{t("auth.email")}</label><input data-testid="register-email" className="z-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div><label className="z-label">{t("auth.phone")}</label><input data-testid="register-phone" className="z-input" dir="ltr" placeholder="07XXXXXXXX" required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><label className="z-label">{t("auth.password")}</label><PasswordInput testId="register-password" minLength={6} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        <label className="flex items-start gap-2 text-sm"><input data-testid="register-terms" type="checkbox" className="mt-1 h-4 w-4 accent-[#06B6D4]" checked={f.accepted_terms} onChange={(e) => setF({ ...f, accepted_terms: e.target.checked })} /><span>{t("auth.accept")} <Link to="/legal/terms" className="font-bold text-[#06B6D4]">{t("auth.terms")}</Link> {t("auth.and")} <Link to="/legal/privacy-policy" className="font-bold text-[#06B6D4]">{t("auth.privacy")}</Link>{f.role === "partner" && <> {t("auth.and")} <Link to="/legal/partner-terms" className="font-bold text-[#06B6D4]">Partner Terms</Link></>}</span></label>
        <button data-testid="register-submit" disabled={busy} className="z-btn-primary w-full">{t("auth.register")}</button>
      </form>
      <p className="mt-6 text-center text-sm">{t("auth.have_account")} <Link to="/login" state={loc.state} data-testid="go-login" className="font-bold text-[#06B6D4]">{t("auth.login")}</Link></p>
    </Shell>
  );
}

export function PasswordInput({ testId, className = "", ...rest }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testId} className={"z-input pe-11 " + className} type={show ? "text" : "password"} {...rest} />
      <button type="button" data-testid={`${testId}-toggle`} aria-label={show ? "hide password" : "show password"} onClick={() => setShow((v) => !v)} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
    </div>
  );
}
